import { gql } from '@apollo/client';
import { pickVerifyRouteFor, type PickVerifyOrderLike } from '@/lib/pickVerifyShip';

/**
 * Home-screen lookup for the picking slip's Code 128. Picking and shipping come back, then —
 * only when neither matches — an order on hold, then a shipped (or shipped and paid,
 * `completed`) order, then a cancelled one; anything else (or nothing) is null. The slip of an
 * order that has since split answers with its -02, so the order returned may not carry the
 * number scanned.
 */
export const WAREHOUSE_SALES_ORDER_BY_NUMBER = gql`
  query warehouseSalesOrderByNumber($companyId: ID!, $vendorSalesOrderNumber: String!) {
    warehouseSalesOrderByNumber(
      companyId: $companyId
      vendorSalesOrderNumber: $vendorSalesOrderNumber
    ) {
      id
      vendorSalesOrderNumber
      vendorStatus
      fulfillmentWorkflow {
        workflow
        stage
      }
    }
  }
`;

const AIM_IDENTIFIER = /^\][A-Za-z][0-9]/;

type SalesOrderScan = {
  serialNumber?: string | null;
  kind?: string | null;
  text?: string | null;
};

/**
 * The sales order number encoded on a picking slip, or null when this scan is a product
 * barcode (or anything else the home screen already knows how to handle).
 *
 * A wedge with "Transmit Code ID" on prefixes Code 128 with an AIM identifier. Numeric order
 * numbers lose that prefix in the GS1 normalizer; an alphanumeric one does not, because the
 * normalizer only strips it when what follows is digits. Strip it here either way.
 */
export const salesOrderNumberFromScan = (result: SalesOrderScan): string | null => {
  if (result.serialNumber) return null;
  if (result.kind !== 'raw') return null;
  let text = (result.text || '').trim();
  if (AIM_IDENTIFIER.test(text)) text = text.slice(3).trim();
  return text || null;
};

/** The vendor pulled this picking order back after its slip was printed; nothing opens for it. */
export const salesOrderIsOnHold = (order: PickVerifyOrderLike): boolean =>
  !!order?.id && order.vendorStatus === 'hold';

/** The order was cancelled after its slip was printed; nothing opens for it. */
export const salesOrderIsCancelled = (order: PickVerifyOrderLike): boolean =>
  !!order?.id && order.vendorStatus === 'cancelled';

/**
 * Where a scanned sales order opens. Picking uses the same screen the Summary's Start button
 * does, including Verify when an Option-2 order has finished Pick. Shipping opens the summary,
 * where Ship is started. Shipped opens the post-ship documents page, as Ship itself does — and so
 * does completed, which is a shipped order that is paid (a paid ship never sits at shipped).
 */
export const salesOrderScreenFor = (order: PickVerifyOrderLike): string | null => {
  if (!order?.id) return null;
  if (order.vendorStatus === 'picking') {
    return pickVerifyRouteFor(order) ?? `/sales-order-picking/${order.id}`;
  }
  if (order.vendorStatus === 'shipping') {
    return `/sales-order-summary/${order.id}`;
  }
  if (order.vendorStatus === 'shipped' || order.vendorStatus === 'completed') {
    return `/sales-order-documents/${order.id}?shipping=true`;
  }
  return null;
};
